import { useMemo } from "react";
import { Box, Divider, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { DashboardDecorationPlacement, DataQueryExecutionResult } from "../../types/dashboard";
import { catalogEntryFor, parseStaticTable } from "../../utils/decorationCatalog";
import { resolveDecorationBindings } from "../../utils/resolveDecorationBindings";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

type Props = {
  item: DashboardDecorationPlacement;
  dataQueries?: DataQueryExecutionResult[];
};

export default function DashboardDecorationView({ item, dataQueries }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.dashboardDecorationView;
  const resolvedItem = useMemo(
    () => (dataQueries?.length ? resolveDecorationBindings(item, dataQueries) : item),
    [item, dataQueries]
  );
  const style = styles.shell(resolvedItem);

  const textOrFallback = (primary?: string, secondary?: string) => {
    const value = primary?.trim() || secondary?.trim();
    if (value) return value;
    const def = catalogEntryFor(item.kind);
    return def.defaultTitle || def.defaultContent || t(`dashboard.decor.kind.${item.kind}`);
  };

  if (item.kind === "spacer") {
    return <Box sx={styles.spacer} aria-hidden />;
  }

  if (item.kind === "divider") {
    const thickness = resolvedItem.style?.dividerThickness ?? 2;
    return (
      <Box sx={style}>
        <Divider sx={styles.dividerInner(thickness, resolvedItem.style?.borderColor)} />
      </Box>
    );
  }

  if (item.kind === "image") {
    const url = resolvedItem.style?.imageUrl?.trim();
    return (
      <Box sx={style}>
        {url ? (
          <Box
            component="img"
            src={url}
            alt={resolvedItem.title || "image"}
            sx={styles.image(resolvedItem.style?.objectFit || "cover", resolvedItem.style?.borderRadius)}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            {t("dashboard.decor.imageUrl")}
          </Typography>
        )}
      </Box>
    );
  }

  if (item.kind === "table") {
    const table = parseStaticTable(resolvedItem.content);
    return (
      <Box sx={style}>
        {resolvedItem.title && (
          <Typography variant="subtitle2" fontWeight={700} sx={styles.tableTitle}>
            {resolvedItem.title}
          </Typography>
        )}
        <Table data-no-stack size="small" sx={styles.table(resolvedItem.style?.fontSize)}>
          <TableHead>
            <TableRow>
              {table.headers.map((h, i) => (
                <TableCell key={`${h}-${i}`} sx={styles.tableHeaderCell}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {table.rows.map((row, ri) => (
              <TableRow key={ri}>
                {table.headers.map((_, ci) => (
                  <TableCell key={ci}>{row[ci] ?? ""}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  }

  if (item.kind === "panel") {
    return (
      <Box sx={style}>
        <Typography variant="subtitle2" fontWeight={700} sx={styles.panelTitle}>
          {textOrFallback(resolvedItem.title, "Panel")}
        </Typography>
        <Typography component="div" variant="body2" sx={[styles.panelBody, styles.headingLabel]}>
          {textOrFallback(resolvedItem.content, resolvedItem.title)}
        </Typography>
      </Box>
    );
  }

  if (item.kind === "heading" || item.kind === "label") {
    return (
      <Box sx={style}>
        <Typography component="div" sx={styles.headingLabel}>
          {textOrFallback(resolvedItem.title, resolvedItem.content)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={style}>
      <Typography component="div" variant="body2" sx={styles.panelBody}>
        {textOrFallback(resolvedItem.content, resolvedItem.title)}
      </Typography>
    </Box>
  );
}
